import { Autocomplete, CircularProgress, TextField, ClickAwayListener, Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SelectOption } from '../../../interfaces/common';
import { LOADING_SPINNER_SIZE } from './constants';

interface ReusableAutocompleteProps {
  label: string;
  options: SelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  multiple?: boolean;
  includeSelectAll?: boolean;
  disabled?: boolean;
  fetchOptions?: (searchTerm: string) => Promise<SelectOption[]>;
  minSearchLength?: number;
  debounceTime?: number;
  defaultSearchTerm?: string;
  placeholder?: string;
  reset?: boolean;
}

export const ReusableAutocomplete: React.FC<ReusableAutocompleteProps> = ({
  label,
  options = [],
  value = [],
  onChange,
  multiple = true,
  includeSelectAll = false,
  disabled = false,
  fetchOptions,
  minSearchLength = 3,
  debounceTime = 300,
  defaultSearchTerm = '',
  placeholder = '',
  reset = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchedOptions, setFetchedOptions] = useState<SelectOption[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedOptionsCache, setSelectedOptionsCache] = useState<Map<string, SelectOption>>(new Map());

  const allOptions = useMemo(() => {
    const baseOptions = [...options, ...fetchedOptions];
    const uniqueOptions = new Map<string, SelectOption>();
    
    // First add cached selected options to ensure they're always visible
    selectedOptionsCache.forEach((option, value) => {
      uniqueOptions.set(value, option);
    });
    
    // Then add other options
    baseOptions.forEach(option => {
      uniqueOptions.set(option.value, option);
    });
    
    const finalOptions = Array.from(uniqueOptions.values());
    return includeSelectAll ? [{ value: 'all', label: 'Select All' }, ...finalOptions] : finalOptions;
  }, [options, fetchedOptions, selectedOptionsCache, includeSelectAll]);

  const loadOptions = useCallback(
    async (searchTerm: string) => {
      if (!fetchOptions) {
        return;
      }

      setLoading(true);
      try {
        const newOptions = await fetchOptions(searchTerm);
        if (Array.isArray(newOptions)) {
          setFetchedOptions(newOptions);
        }
      } catch (error) {
        console.error(`Error loading options for search term "${searchTerm}":`, error);
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions],
  );

  useEffect(() => {
    loadOptions(defaultSearchTerm);
  }, [loadOptions, defaultSearchTerm]);

  // Update selectedOptionsCache when we have new data
  useEffect(() => {
    const allAvailableOptions = [...options, ...fetchedOptions];
    
    setSelectedOptionsCache(prevCache => {
      const newCache = new Map(prevCache);
      
      // Add any new options that match current values
      allAvailableOptions.forEach(option => {
        if (value.includes(option.value)) {
          newCache.set(option.value, option);
        }
      });
      
      // Remove options that are no longer selected
      newCache.forEach((_, optionValue) => {
        if (!value.includes(optionValue)) {
          newCache.delete(optionValue);
        }
      });
      
      return newCache;
    });
  }, [value, options, fetchedOptions]);

  useEffect(() => {
    if (!fetchOptions || !open || inputValue.length < minSearchLength) {
      return;
    }

    const timer = setTimeout(() => {
      loadOptions(inputValue);
    }, debounceTime);

    return () => clearTimeout(timer);
  }, [inputValue, fetchOptions, minSearchLength, debounceTime, open, loadOptions]);

  useEffect(() => {
    if (reset) {
      setInputValue('');
      setFetchedOptions([]);
      loadOptions(defaultSearchTerm);
    }
  }, [reset, defaultSearchTerm, loadOptions]);

  const selectedOptions = useMemo(() => {
    const optionsMap = new Map(allOptions.map((opt) => [opt.value, opt]));
    return value
      .map((v) => optionsMap.get(v))
      .filter((opt): opt is SelectOption => opt !== undefined);
  }, [value, allOptions]);

  const handleChange = async (
    _: React.SyntheticEvent,
    newValue: SelectOption | SelectOption[] | null,
    reason: string,
  ) => {
    // Only clear selections for specific actions, not when typing/searching
    if (reason === 'clear') {
      onChange([]);
      setInputValue('');
      loadOptions(defaultSearchTerm);
      return;
    }

    // Handle removing individual options in multi-select
    if (reason === 'removeOption') {
      if (Array.isArray(newValue)) {
        onChange(newValue.map((opt) => opt.value));
      } else {
        onChange([]);
      }
      return;
    }

    // Don't process if newValue is null/undefined and it's not a clear action
    // This happens when typing in the search field
    if (!newValue) {
      return;
    }

    if (Array.isArray(newValue)) {
      if (includeSelectAll && newValue.some((opt) => opt.value === 'all')) {
        const allValues = allOptions.filter((opt) => opt.value !== 'all').map((opt) => opt.value);
        onChange(value.length === allValues.length ? [] : allValues);
        setInputValue(''); // Clear search input
        if (defaultSearchTerm) {
          loadOptions(defaultSearchTerm);
        }
        return;
      }

      onChange(newValue.map((opt) => opt.value));
    } else {
      onChange([newValue.value]);
    }
    
    // Clear search input after selection to show unfiltered list
    setInputValue('');
    if (defaultSearchTerm) {
      loadOptions(defaultSearchTerm);
    }
  };

  const handleClickAway = () => {
    if (open) {
      setOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <Autocomplete
      size='small'
      disabled={disabled}
      multiple={multiple}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={(_, reason) => {
        // For multiselect, don't close when selecting an option
        // But always allow closing for escape key, blur, and other reasons
        if (multiple && reason === 'selectOption') {
          return; // Keep open for more selections
        }
        setOpen(false);
      }}
      options={allOptions}
      loading={loading}
      value={multiple ? selectedOptions : selectedOptions[0] || null}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      disableCloseOnSelect={false}
      clearOnBlur={false}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant='filled'
          key={`option-${value}`}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color='inherit' size={LOADING_SPINNER_SIZE} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
      </Box>
    </ClickAwayListener>
  );
};
